/**
 * Shared helpers for trip list display with multiload grouping.
 * - Sort trips by sequence and multiload route order (P1, P2, P3).
 * - Group consecutive trips into sections (multiload group vs single trip).
 */

/**
 * Sort trips for display: by sequence, and within same multiload group by pickup order (P1, P2, P3).
 * @param {Array} trips
 * @returns {Array} sorted trip array
 */
export function sortTripsForDisplay(trips) {
  if (!trips?.length) return [];
  const base = [...trips].sort((a, b) => {
    const seqA = a.sequence ?? 999;
    const seqB = b.sequence ?? 999;
    if (a.multiload_group_id != null && b.multiload_group_id != null && String(a.multiload_group_id) === String(b.multiload_group_id)) {
      return (a.multiload_pickup_order ?? 99) - (b.multiload_pickup_order ?? 99);
    }
    return seqA - seqB;
  });

  // Keep each multiload group contiguous in UI ordering so a single trip
  // cannot visually split a group when backend sequence is noisy.
  const grouped = {};
  for (let i = 0; i < base.length; i += 1) {
    const t = base[i];
    if (t?.is_multiload && t?.multiload_group_id != null) {
      const gid = String(t.multiload_group_id);
      if (!grouped[gid]) grouped[gid] = [];
      grouped[gid].push({ idx: i, trip: t });
    }
  }

  const out = [];
  const consumed = new Set();
  for (let i = 0; i < base.length; i += 1) {
    if (consumed.has(i)) continue;
    const t = base[i];
    if (t?.is_multiload && t?.multiload_group_id != null) {
      const gid = String(t.multiload_group_id);
      const members = grouped[gid] || [];
      for (const m of members) consumed.add(m.idx);
      members
        .map((m) => m.trip)
        .sort((a, b) => {
          const pa = Number(a.multiload_pickup_order ?? 999);
          const pb = Number(b.multiload_pickup_order ?? 999);
          if (pa !== pb) return pa - pb;
          return (a.sequence ?? 999) - (b.sequence ?? 999);
        })
        .forEach((m) => out.push(m));
    } else {
      consumed.add(i);
      out.push(t);
    }
  }

  return out;
}

/**
 * Group sorted trips into sections: consecutive trips with same multiload_group_id become one section.
 * @param {Array} sortedTrips - must be sorted (e.g. via sortTripsForDisplay)
 * @returns {Array<{ multiloadGroupId: string|null, trips: Array }>}
 */
export function buildTripSections(sortedTrips) {
  if (!sortedTrips?.length) return [];
  const sections = [];
  let i = 0;
  while (i < sortedTrips.length) {
    const trip = sortedTrips[i];
    if (trip.is_multiload && trip.multiload_group_id != null) {
      const gid = String(trip.multiload_group_id);
      const groupTrips = [];
      while (i < sortedTrips.length && String(sortedTrips[i].multiload_group_id) === gid) {
        groupTrips.push(sortedTrips[i]);
        i++;
      }
      sections.push({ multiloadGroupId: gid, trips: groupTrips });
    } else {
      sections.push({ multiloadGroupId: null, trips: [trip] });
      i++;
    }
  }
  return sections;
}
